import { CSSProperties, FC, ReactNode } from "react";

import styled from "@emotion/styled";

const RING_MASK =
  "radial-gradient(circle farthest-side, transparent calc(100% - var(--ring-stroke)), #000 calc(100% - var(--ring-stroke)))";

const pointOnRing = (
  angle: number,
  size: number,
  radius: number
): [number, number] => {
  const radians = (angle * Math.PI) / 180;
  return [
    size / 2 + radius * Math.sin(radians),
    size / 2 - radius * Math.cos(radians)
  ];
};

const segmentMask = (
  segments: RingSegment[],
  size: number,
  strokeWidth: number
): string => {
  const radius = (size - strokeWidth) / 2;
  const paths = segments
    .map(([start, end]) => {
      const [startX, startY] = pointOnRing(start, size, radius);
      const [endX, endY] = pointOnRing(end, size, radius);
      const largeArc = Math.abs(end - start) > 180 ? 1 : 0;
      return `<path d="M${startX.toFixed(3)} ${startY.toFixed(3)}A${radius} ${radius} 0 ${largeArc} 1 ${endX.toFixed(3)} ${endY.toFixed(3)}"/>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}"><g fill="none" stroke="white" stroke-width="${strokeWidth}" stroke-linecap="round">${paths}</g></svg>`;
  return `url('data:image/svg+xml,${encodeURIComponent(svg)}')`;
};

const COLOR_CHANNELS = /[\d.]+/g;

type Rgba = [number, number, number, number];

/** Parses `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb()` and `rgba()` into RGBA channels. */
const parseColor = (value: string): Rgba | undefined => {
  const input = value.trim();

  if (input.startsWith("#")) {
    const digits = input.slice(1);
    const hex =
      digits.length === 3 || digits.length === 4
        ? digits.replace(/./g, (digit) => digit + digit)
        : digits;
    if (hex.length !== 6 && hex.length !== 8) {
      return undefined;
    }
    const channels = [0, 2, 4, 6]
      .map((start) => hex.slice(start, start + 2))
      .filter((pair) => pair.length === 2)
      .map((pair) => parseInt(pair, 16));
    if (channels.some(Number.isNaN)) {
      return undefined;
    }
    const [red, green, blue, alpha] = channels;
    return [red, green, blue, alpha === undefined ? 1 : alpha / 255];
  }

  if (input.startsWith("rgb")) {
    const channels = (input.match(COLOR_CHANNELS) ?? []).map(Number);
    if (channels.length < 3 || channels.some(Number.isNaN)) {
      return undefined;
    }
    const [red, green, blue, alpha] = channels;
    return [red, green, blue, alpha === undefined ? 1 : alpha];
  }

  return undefined;
};

/**
 * The colour `ratio` of the way from `from` to `to`. Used to end a clipped
 * gradient on the exact shade it had reached, rather than on its last stop.
 * Falls back to `from` for colours that are not plain hex/rgb (e.g. a CSS var).
 */
const mixColors = (from: string, to: string, ratio: number): string => {
  const start = parseColor(from);
  const end = parseColor(to);
  if (!start || !end) {
    return from;
  }
  const channel = (index: number): number =>
    start[index] + (end[index] - start[index]) * ratio;
  const [red, green, blue] = [0, 1, 2].map((index) =>
    Math.round(channel(index))
  );
  return `rgba(${red}, ${green}, ${blue}, ${Number(channel(3).toFixed(3))})`;
};

interface ArcStops {
  /** Conic-gradient colour stops covering the filled arc. */
  stops: string[];
  /** Colour the arc has reached at its end, for the rounded end cap. */
  endColor: string;
}

const buildArcStops = (
  colors: string[],
  sweep: number,
  gradientArc: number
): ArcStops => {
  const lastColor = colors[colors.length - 1];

  if (colors.length === 1) {
    return {
      stops: [`${colors[0]} 0deg`, `${colors[0]} ${sweep.toFixed(2)}deg`],
      endColor: colors[0]
    };
  }

  const angles = colors.map(
    (_, index) => (index / (colors.length - 1)) * gradientArc
  );
  const visible = angles.filter((angle) => angle <= sweep).length;
  const stops = colors
    .slice(0, visible)
    .map((color, index) => `${color} ${angles[index].toFixed(2)}deg`);

  if (visible === colors.length) {
    if (sweep > gradientArc) {
      stops.push(`${lastColor} ${sweep.toFixed(2)}deg`);
    }
    return { stops, endColor: lastColor };
  }

  const before = angles[visible - 1];
  const after = angles[visible];
  const endColor = mixColors(
    colors[visible - 1],
    colors[visible],
    (sweep - before) / (after - before)
  );
  stops.push(`${endColor} ${sweep.toFixed(2)}deg`);
  return { stops, endColor };
};

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: var(--ring-size);
  height: var(--ring-size);
`;

const Track = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--ring-track);
  -webkit-mask: var(--ring-mask) center / 100% 100% no-repeat;
  mask: var(--ring-mask) center / 100% 100% no-repeat;
`;

const ArcGroup = styled.div`
  position: absolute;
  inset: 0;
  opacity: var(--ring-arc-opacity);
`;

const Arc = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--ring-arc);
  -webkit-mask: var(--ring-mask) center / 100% 100% no-repeat;
  mask: var(--ring-mask) center / 100% 100% no-repeat;
`;

const Cap = styled.span`
  position: absolute;
  left: var(--cap-x);
  top: var(--cap-y);
  width: var(--ring-stroke);
  height: var(--ring-stroke);
  border-radius: 50%;
  background: var(--cap-color);
  transform: translate(-50%, -50%);
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type RingSegment = [number, number];

export interface RingProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  progressColor?: string;
  progressGradient?: string[];
  gradientSweep?: number;
  segments?: RingSegment[];
  progressOpacity?: number;
  children?: ReactNode;
  className?: string;
  "aria-label"?: string;
}

const Ring: FC<RingProps> = ({
  progress,
  size = 80,
  strokeWidth = 6,
  trackColor = "var(--color-gray-lighter)",
  progressColor = "var(--color-charcoal)",
  progressGradient,
  gradientSweep,
  segments,
  progressOpacity = 1,
  children,
  className,
  "aria-label": ariaLabel
}) => {
  const clamped = Math.min(Math.max(progress, 0), 1);
  const sweep = clamped * 360;
  const hasGradient = progressGradient != null && progressGradient.length > 0;
  const colors = hasGradient ? progressGradient : [progressColor];

  const gradientArc = Math.min(Math.max(gradientSweep ?? sweep, 0), 360);
  const { stops, endColor } = buildArcStops(colors, sweep, gradientArc);

  const centerlineRadius = (size - strokeWidth) / 2;
  const capSpread = ((strokeWidth / 2 / centerlineRadius) * 180) / Math.PI;

  const wrapStart = segments?.length
    ? 360 + Math.min(...segments.map(([start]) => start)) - capSpread
    : undefined;

  const arcStops = [...stops];
  if (clamped < 1) {
    arcStops.push(`transparent ${sweep.toFixed(2)}deg`);
    if (wrapStart !== undefined && wrapStart > sweep && wrapStart < 360) {
      arcStops.push(
        `transparent ${wrapStart.toFixed(2)}deg`,
        `${colors[0]} ${wrapStart.toFixed(2)}deg`
      );
    }
  }
  const arcImage = `conic-gradient(from 0deg, ${arcStops.join(", ")})`;

  const capAt = (angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: size / 2 + centerlineRadius * Math.sin(rad),
      y: size / 2 - centerlineRadius * Math.cos(rad)
    };
  };
  // A broken ring already gets rounded ends from its mask, so it only needs a
  // cap where the fill stops part-way along a segment.
  const inSegment = (angle: number): boolean =>
    segments?.some(([start, end]) => angle >= start && angle <= end) ?? true;
  const partiallyFilled = clamped > 0 && clamped < 1;
  const showStartCap = partiallyFilled && !segments?.length;
  const showEndCap = partiallyFilled && inSegment(sweep);
  const startCap = capAt(0);
  const endCap = capAt(sweep);

  const wrapperStyle = {
    "--ring-size": `${size}px`,
    "--ring-stroke": `${strokeWidth}px`,
    "--ring-track": trackColor,
    "--ring-arc": arcImage,
    "--ring-mask": segments?.length
      ? segmentMask(segments, size, strokeWidth)
      : RING_MASK,
    "--ring-arc-opacity": progressOpacity
  } as CSSProperties;

  const startCapStyle = {
    "--cap-x": `${startCap.x}px`,
    "--cap-y": `${startCap.y}px`,
    "--cap-color": colors[0]
  } as CSSProperties;

  const endCapStyle = {
    "--cap-x": `${endCap.x}px`,
    "--cap-y": `${endCap.y}px`,
    "--cap-color": endColor
  } as CSSProperties;

  return (
    <Wrapper
      style={wrapperStyle}
      className={className}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <Track />
      <ArcGroup>
        <Arc />
        {showStartCap && <Cap style={startCapStyle} />}
        {showEndCap && <Cap style={endCapStyle} />}
      </ArcGroup>
      {children != null && <Center>{children}</Center>}
    </Wrapper>
  );
};

export default Ring;
